import { createClient } from '@supabase/supabase-js';

// Debug environment variables
console.log('🔧 Environment variables check:');
console.log('VITE_SUPABASE_URL:', import.meta.env.VITE_SUPABASE_URL);
console.log('VITE_SUPABASE_ANON_KEY:', import.meta.env.VITE_SUPABASE_ANON_KEY ? '[PRESENT]' : '[MISSING]');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Make Supabase optional - only initialize if environment variables are present
let supabase: ReturnType<typeof createClient> | null = null;

if (supabaseUrl && supabaseAnonKey) {
  console.log('✅ Supabase environment variables found, initializing client');
  console.log('🔗 Connecting to:', supabaseUrl);
  supabase = createClient(supabaseUrl, supabaseAnonKey);
} else {
  console.error('❌ Supabase environment variables not found!');
  console.error('Missing variables:', {
    VITE_SUPABASE_URL: !supabaseUrl,
    VITE_SUPABASE_ANON_KEY: !supabaseAnonKey
  });
  console.error('Please create a .env file in your project root with:');
  console.error('VITE_SUPABASE_URL=your_supabase_url');
  console.error('VITE_SUPABASE_ANON_KEY=your_supabase_anon_key');
}

export { supabase };

// Export typed client for better TypeScript support
export type SupabaseClient = typeof supabase;

// Helper function to check if Supabase is available
export const isSupabaseAvailable = (): boolean => {
  return supabase !== null;
};

/**
 * Extract the current tenant ID (subdomain) from the hostname
 */
export const getCurrentTenantId = (): string | null => {
  const hostname = window.location.hostname;
  
  // Development environment - always show tenant selector
  const isDevelopment = hostname.includes('bolt.new') || hostname === 'localhost' || hostname.includes('192.168');
  if (isDevelopment) {
    return 'test-selector';  // Always show selector in development
  }
  
  // Production environment
  if (hostname.includes('yoursanctuary.ai')) {
    const subdomain = hostname.split('.')[0];
    if (subdomain === 'test') {
      return 'test-selector';
    }
    return subdomain;  // demo, gmct, smct, etc.
  }
  
  return 'test-selector'; // Fallback
};

/**
 * Create tenant-scoped queries for Supabase
 * This helper ensures all queries are automatically filtered by the current tenant
 */
export const tenantQuery = {
  /**
   * Get a query builder for cemeteries table scoped to current tenant
   */
  cemeteries: () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }
    
    return supabase.from('cemeteries').select('*').eq('id', tenantId);
  },

  /**
   * Get a query builder for cemetery_locations table scoped to current tenant
   */
  locations: () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }
    
    return supabase.from('cemetery_locations').select('*').eq('cemetery_id', tenantId);
  },

  /**
   * Get a query builder for graves table scoped to current tenant
   */
  graves: () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }
    
    return supabase.from('graves').select('*').eq('cemetery_id', tenantId);
  },

  /**
   * Get a query builder for funerals table scoped to current tenant
   */
  funerals: () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }
    
    // Handle test subdomain - tenant selector
    if (hostname === 'test.yoursanctuary.ai') {
      console.log('🧪 Test subdomain - tenant selector');
      return 'test-selector';
    }
    
    return supabase.from('funerals').select('*').eq('cemetery_id', tenantId);
  },

  /**
   * Get a query builder for work_requests table scoped to current tenant
   */
  workRequests: () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }
    
    return supabase.from('work_requests').select('*').eq('cemetery_id', tenantId);
  },

  /**
   * Get a query builder for knowledge_chunks table scoped to current tenant
   */
  knowledgeChunks: () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }
    
    return supabase.from('knowledge_chunks').select('*').eq('cemetery_id', tenantId);
  },

  /**
   * Generic query builder for any table with cemetery_id column
   */
  table: (tableName: string) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    
    const tenantId = getCurrentTenantId();
    if (!tenantId) {
  // Handle bolt.new development - show tenant selector
  if (hostname.includes('bolt.new')) {
    console.log('🧪 Bolt.new development - tenant selector');
    return 'test-selector';
  }
  
      throw new Error('No tenant ID found');
    }
    
    return supabase.from(tableName).select('*').eq('cemetery_id', tenantId);
  }
};